import * as actionTypes from "../actionTypes";
import * as apiBinders from "../lib/api/binders";
import * as binderAlertRateLabels from "../enums/binderAlertRateLabels";
import * as binderAlertRates from "../enums/binderAlertRates";
import * as binderContentKeys from "../enums/binderContentKeys";
import * as binderErrors from "../enums/binderErrors";
import * as binderObserversActions from "./binderObserversActions";
import * as binderOrderItemManip from "../lib/binderOrderItemManip";
import * as binderTabsActions from "./binderTabsActions";
import * as binderTransforms from "../lib/binder/binderTransforms";
import * as binderTypes from "../enums/binderTypes";
import * as dateUtil from "../lib/dateUtil";
import * as fileDownloadUtil from "../lib/fileDownloadUtil";
import * as manageAlertColumns from "../enums/manageAlertColumns";
import * as queryStringFields from "../enums/queryStringFields";
import * as reservedBinderIds from "../enums/binderIds";
import * as routeHelpers from "../lib/routeHelpers";
import * as selectorFactory from "../lib/selectorFactory";
import * as stringUtil from "../lib/stringUtil";
import * as tilesActions from "./tilesActions";
import * as tileSectionsActions from "./tileSectionsActions";
import * as uiActions from "./uiActions";
import copyBinderTab from "../lib/copyBinderTab";
import find from "lodash.find";
import findIndex from "lodash.findindex";
import getApiTabToSave from "../lib/getApiTabToSave";
import getObserverLabel from "../lib/getObserverLabel";
import isProfileBinder from "../lib/binder/isProfileBinder";
import uuid from "uuid";

const strings = {
	copiedFromBinder: "Copied from binder",
};

export function saveBinderRequest(binderId, tabIndex, message) {
	return (dispatch, getState) => {
		const state = getState();
		const binder = state.binders[binderId];
		const binderTabId = state.ui.binderTabId;

		if (!binder) {
			if (__DEV__) {
				console.warn("Attempting to save a binder that cannot be found.");
			}
			return Promise.reject(binderErrors.BINDER_NOT_FOUND);
		}

		const binderTabs = binder.tabIds
			? binder.tabIds.map(tabId => state.binderTabs[tabId])
			: null;

		if (
			binder.id !== reservedBinderIds.UNASSIGNED &&
			!binder.dirty &&
			!find(binderTabs, t => t.dirty || (t.clientId !== undefined && t.clientId !== null))
		) {
			if (__DEV__) {
				console.warn("Attempting to save a binder that is not dirty.");
			}
			return Promise.reject(binderErrors.BINDER_INVALID_OPERATION);
		}

		const tabs = binderTabs ? binderTabs.map(getApiTabToSave) : null;

		// Save binder.content with post-save tab ids
		const orderItems = binder.tabIds
			? binderOrderItemManip.getSavedTabIds(binder.orderItems, binder.tabIds, binder.id)
			: binder.orderItems;
		const content = {
			...binder.content,
			[binderContentKeys.ORDER_ITEMS]: orderItems,
		};

		let binderUpdate;
		if (binderId === reservedBinderIds.UNASSIGNED) {
			const includeBinderType =
				binder.type !== binderTypes.BINDER && !isProfileBinder(binder.type);
			binderUpdate = apiBinders.create(
				binder.title,
				binder.libraryName,
				tabs,
				binder.clientcode,
				binder.description,
				content,
				message,
				includeBinderType ? binder.type : undefined,
			);
		} else {
			binderUpdate = apiBinders.update(
				binderId,
				tabs,
				binder.title,
				binder.clientcode,
				binder.description,
				content,
				message,
			);
		}

		let finalBinderId = binderId;
		const finalTabIndex = findIndex(binder.tabIds, id => id === binderTabId);

		return binderUpdate
			.then(response => {
				dispatch(setBinder(response));

				if (binderId === reservedBinderIds.UNASSIGNED) {
					finalBinderId = response.id;
				}

				return response;
			})
			.then(createdBinder => {
				dispatch(saveBinder(finalBinderId));

				let path;
				const binderIdChanged = binderId !== finalBinderId;
				const tabIndexChanged = tabIndex !== finalTabIndex;

				if (binderIdChanged || tabIndexChanged) {
					path =
						finalTabIndex !== -1
							? routeHelpers.getBinderTabPath(createdBinder, finalTabIndex)
							: routeHelpers.getBinderPath(createdBinder);
					path = Object.keys(window.location.search).length
						? `${path}${window.location.search}`
						: path;
				}
				return { redirectPath: path, finalBinderId: finalBinderId };
			});
	};
}

export function saveBinder(binderId) {
	return {
		type: actionTypes.BINDERS_SAVE,
		binderId,
	};
}

export function getProfileQueryParams(query) {
	const profileType = query ? query[queryStringFields.PROFILE_TYPE] : null;

	let profileIds = [];
	const queryProfileId = query ? query[queryStringFields.PROFILE_ID] : null;

	let queryProfileIds = null;
	if (query && query[queryStringFields.PROFILE_IDS]) {
		queryProfileIds = query[queryStringFields.PROFILE_IDS];
	} else if (query && query[queryStringFields.PROFILE_IDS_2]) {
		queryProfileIds = query[queryStringFields.PROFILE_IDS_2];
	}

	if (queryProfileId) {
		profileIds.push(queryProfileId);
	}
	if (queryProfileIds) {
		profileIds = profileIds.concat(queryProfileIds);
	}
	profileIds = profileIds.map(id => {
		const parsed = parseInt(id, 10);
		return isNaN(parsed) ? id : parsed;
	});

	return { profileType, profileIds: profileIds.length ? profileIds : null };
}

export function getCurrentBinder(params, query) {
	return (dispatch, getState) => {
		const state = getState();
		const { binderType, libraryName } = params;
		const binderId = parseInt(params.binderId, 10);
		const { profileType, profileIds } = getProfileQueryParams(query);
		const searchMemo = query ? query[queryStringFields.SEARCH_MEMO] : null;
		const canFetchBinderFromQueryParams = (profileType && profileIds) || !!searchMemo;

		if (
			!state.binders[binderId] &&
			binderId === reservedBinderIds.UNASSIGNED &&
			!canFetchBinderFromQueryParams
		) {
			return Promise.reject(new Error(binderErrors.BINDER_NOT_FOUND));
		}

		// Only fetch binder if not already in redux store
		let loadData = Promise.resolve();
		if (
			!state.binders[binderId] ||
			state.binders[binderId].tabIds === null ||
			state.binders[binderId].tabIds === undefined
		) {
			if (profileType && profileIds) {
				loadData = apiBinders.getDRP(libraryName, profileType, profileIds);
			} else if (searchMemo) {
				loadData = apiBinders.getSearchBinder(libraryName, searchMemo);
			} else {
				loadData = apiBinders.getBinder(libraryName, binderType, binderId);
			}
		}

		return loadData.then(binder => {
			if (!binder) {
				return dispatch(uiActions.setBinderFromRoute({ params }));
			}

			const customizedBinder = isProfileBinder(binder.type)
				? binderTransforms.applyUserLayouts(binder, state.session.currentUser.content)
				: binder;

			// Use existing observers if binder is already partially loaded
			const observerIds =
				state.binders[binderId] && !state.binders[binderId].tabIds
					? state.binders[binderId].observerIds
					: null;

			if (observerIds) {
				dispatch(setBinderWithObservers(customizedBinder, observerIds));
			} else {
				dispatch(setBinder(customizedBinder));
			}

			return dispatch(uiActions.setBinderFromRoute({ params }));
		});
	};
}

export function reloadBinder(binderId) {
	return (dispatch, getState) => {
		const state = getState();
		const binder = state.binders[binderId];

		if (!binder) {
			return Promise.reject(new Error(binderErrors.BINDER_NOT_FOUND));
		}

		const content = state.session.currentUser ? state.session.currentUser.content : null;

		return apiBinders.getBinder(binder.libraryName, binder.type, binderId).then(apiBinder => {
			const customizedBinder = isProfileBinder(apiBinder.type)
				? binderTransforms.applyUserLayouts(apiBinder, content)
				: apiBinder;
			dispatch(setBinder(customizedBinder));

			dispatch(saveBinder(apiBinder.id));

			let path;
			if (apiBinder.tabs.length) {
				path = routeHelpers.getBinderTabPath(apiBinder, 0);
			} else {
				path = routeHelpers.getBinderPath(apiBinder);
			}

			return path;
		});
	};
}

export function setBinder(apiBinder) {
	return (dispatch, getState) => {
		const state = getState();
		if (state.binders[apiBinder.id]) {
			dispatch(clearBinderTiles(apiBinder.id));
		}

		const { observers } = dispatch(binderObserversActions.addObservers(apiBinder.observers));
		const observerIds = observers.map(o => o.observerId);

		dispatch(setBinderWithObservers(apiBinder, observerIds));
	};
}

export function setBinderWithObservers(binder, observerIds) {
	return {
		type: actionTypes.BINDERS_SET,
		binder,
		observerIds,
	};
}

function hasSamePrivileges(apiBinder, reduxBinder) {
	return (
		apiBinder.editable === reduxBinder.editable &&
		apiBinder.shareable === reduxBinder.shareable &&
		apiBinder.bookmarked === reduxBinder.bookmarked &&
		apiBinder.alert_rate === reduxBinder.alertRate &&
		apiBinder.alert_history === reduxBinder.alertHistory
	);
}

export function setBindersCatalog(binders) {
	return (dispatch, getState) => {
		const state = getState();

		const bindersWithObservers = [];
		const unloadedBinders = binders.filter(
			b => !state.binders[b.id] || !hasSamePrivileges(b, state.binders[b.id]),
		);
		if (!unloadedBinders.length) {
			return;
		}

		const groupedObservers = unloadedBinders.map(b => b.observers);
		const withIds = dispatch(binderObserversActions.addGroupedObservers(groupedObservers));

		unloadedBinders.forEach((binder, index) => {
			let observerIds = [];
			const binderObservers = withIds[index];

			if (binderObservers && binderObservers.length) {
				observerIds = binderObservers.map(o => o.observerId);
			}

			bindersWithObservers.push({
				binder: {
					...binder,
					tabs: null,
				},
				observerIds,
			});
		});

		if (!bindersWithObservers.length) {
			return;
		}

		dispatch({
			type: actionTypes.BINDERS_SET_MULTIPLE,
			binders: bindersWithObservers,
		});
	};
}

export function removeBinderRequest(binderId) {
	return (dispatch, getState) => {
		const state = getState();
		const binder = state.binders[binderId];

		if (!binder) {
			return Promise.reject(binderErrors.BINDER_NOT_FOUND);
		}

		return apiBinders.deleteBinder(binderId, binder.type, binder.libraryName);
	};
}

export function removeBinder(binderId) {
	return (dispatch, getState) => {
		const state = getState();
		const binder = state.binders[binderId];

		if (!binder) {
			return;
		}

		const tabIds = binder.tabIds || [];
		const tileSectionIds = tabIds
			.map(tabId => state.binderTabs[tabId])
			.reduce((acc, tab) => {
				return tab && tab.sectionIdNames
					? [...acc, ...tab.sectionIdNames.map(idName => idName.id).filter(id => !!id)]
					: acc;
			}, []);
		const tileIds = tileSectionIds.reduce((acc, sectionId) => {
			const section = state.tileSections[sectionId];
			return !section || !section.tileIds ? acc : [...acc, ...section.tileIds];
		}, []);

		dispatch(binderObserversActions.removeObservers(binder.observerIds));
		dispatch({
			type: actionTypes.BINDERS_REMOVE,
			binderId,
			tileSectionIds,
			tileIds,
		});
	};
}

export function setBinderOwner(binderId, ownerName, ownerEmail, ownerUserId) {
	return {
		type: actionTypes.BINDERS_SET_OWNER,
		binderId,
		ownerName,
		ownerEmail,
		ownerUserId,
	};
}

export function setBinderEditable(binderId, editable) {
	return {
		type: actionTypes.BINDERS_SET_EDITABLE,
		binderId,
		editable,
	};
}

export function setBinderBookmarkedRequest(binderId, bookmark) {
	return (dispatch, getState) => {
		const state = getState();
		const binder = state.binders[binderId];
		if (!binder) {
			if (__DEV__) {
				console.warn("Attempting to bookmark binder that cannot be found.");
			}
			return Promise.reject(binderErrors.BINDER_NOT_FOUND);
		}

		dispatch(setBinderBookmarked(binderId, bookmark));

		return apiBinders
			.setBookmark(binder.type, binderId, bookmark, binder.libraryName)
			.catch(err => {
				dispatch(setBinderBookmarked(binderId, !bookmark));
				return Promise.reject(err);
			});
	};
}

export function setBinderBookmarked(binderId, bookmarked) {
	return {
		type: actionTypes.BINDERS_SET_BOOKMARKED,
		binderId,
		bookmarked,
	};
}

export function setBinderAlertRequest(binderId, userAlertRate, userAlertHistory) {
	return (dispatch, getState) => {
		const state = getState();
		const binder = state.binders[binderId];
		const currentUserId = state.session.currentUser.id;

		if (!binder) {
			if (__DEV__) {
				console.warn("Attempting to change alert rate for a binder that cannot be found.");
			}
			return Promise.reject(binderErrors.BINDER_NOT_FOUND);
		}

		if (userAlertRate === null || userAlertRate === undefined) {
			if (__DEV__) {
				console.warn("AlertRate is required to change alert rate.");
			}
			return Promise.reject(binderErrors.BINDER_INVALID_OPERATION);
		}

		const alertRate = userAlertRate;
		const alertHistory =
			userAlertHistory !== null && userAlertHistory !== undefined
				? userAlertHistory
				: binder.alertHistory;

		return apiBinders
			.setAlert(
				currentUserId,
				binder.type,
				binderId,
				alertRate,
				alertHistory,
				binder.libraryName,
			)
			.then(() => {
				const observers = binder.observerIds.map(id => state.binderObservers[id]);
				const observer = find(observers, ob => ob.userId === currentUserId);
				if (observer) {
					dispatch(
						binderObserversActions.updateObserver(
							observer.__id,
							alertRate,
							alertHistory,
						),
					);
				}

				if (userAlertHistory !== undefined && userAlertHistory !== null) {
					dispatch(setBinderAlertHistory(binderId, alertHistory));
				}
				return dispatch(setBinderAlertRate(binderId, alertRate));
			});
	};
}

export function setBinderAlertRate(binderId, alertRate) {
	return {
		type: actionTypes.BINDERS_SET_ALERT_RATE,
		binderId,
		alertRate,
	};
}

export function setBinderAlertHistory(binderId, alertHistory) {
	return {
		type: actionTypes.BINDERS_SET_ALERT_HISTORY,
		binderId,
		alertHistory,
	};
}

export function setBinderTitle(binderId, title) {
	return {
		type: actionTypes.BINDERS_SET_TITLE,
		binderId,
		title,
	};
}

export function setBinderClientCode(binderId, clientcode) {
	return {
		type: actionTypes.BINDERS_SET_CLIENT_CODE,
		binderId,
		clientcode,
	};
}

export function setBinderDescription(binderId, description) {
	return {
		type: actionTypes.BINDERS_SET_DESCRIPTION,
		binderId,
		description,
	};
}

export function reorderBinderItems(binderId, orderItems) {
	return {
		type: actionTypes.BINDERS_REORDER_ITEMS,
		binderId,
		orderItems,
	};
}

export function relabelBinderFolder(binderId, folderId, folderLabel) {
	return {
		type: actionTypes.BINDERS_RELABEL_FOLDER,
		binderId,
		folderId,
		folderLabel,
	};
}

export function setBinderObservers(binderId, observerIds) {
	return {
		type: actionTypes.BINDERS_SET_OBSERVERS,
		binderId,
		observerIds,
	};
}

export function addBinderObserver(binderId, observerId) {
	return {
		type: actionTypes.BINDERS_ADD_OBSERVER,
		binderId,
		observerId,
	};
}

export function copyBinderRequest(binderId, copyBinderTitle, keepAlert) {
	return (dispatch, getState) => {
		const state = getState();
		const binder = state.binders[binderId];
		const user = state.session.currentUser;

		if (!binder) {
			if (__DEV__) {
				console.warn("Attempting to copy binder that cannot be found");
			}
			return Promise.reject(binderErrors.BINDER_NOT_FOUND);
		}

		if (!user) {
			if (__DEV__) {
				console.warn("Must be logged in to copy a binder");
			}
			return Promise.reject(binderErrors.BINDER_UNAUTHORIZED_OPERATION);
		}

		let createdBinder = null;

		const binderTabs = binder.tabIds.map(tabId => {
			const tab = state.binderTabs[tabId];
			return copyBinderTab(tab, binder.type, binder.libraryName, binder.id, true);
		});
		const tabs = binderTabs.map(getApiTabToSave);

		const orderItems = binderOrderItemManip.getSavedTabIds(
			binder.orderItems,
			binder.tabIds,
			binder.id,
		);
		const content = {
			...binder.content,
			[binderContentKeys.ORDER_ITEMS]: orderItems,
		};

		return apiBinders
			.create(
				copyBinderTitle ? copyBinderTitle : binder.title,
				binder.libraryName,
				tabs,
				binder.clientcode,
				binder.description,
				content,
				`${strings.copiedFromBinder} "${binder.title}".`,
			)
			.then(response => {
				createdBinder = response;
				return dispatch(setBinder(createdBinder));
			})
			.then(() => {
				if (!keepAlert) {
					return;
				}

				let alertRate = binderAlertRates.NONE;
				let alertHistory = false;
				if (binder.ownerUserId === user.id) {
					alertRate = binder.alertRate;
					alertHistory = binder.alertHistory;
				} else {
					const observers = binder.observerIds.map(id => state.binderObservers[id]);
					const observer = find(observers, ob => ob.userId === user.id);

					if (!observer) {
						return Promise.resolve();
					}

					alertRate = observer.alertRate;
					alertHistory = observer.alertHistory;
				}

				return dispatch(setBinderAlertRequest(createdBinder.id, alertRate, alertHistory));
			})
			.then(() => {
				dispatch(saveBinder(createdBinder.id));
				return createdBinder;
			});
	};
}

export function addBinderOrderItem(binderId, insertAfterId, label, itemType, id, children) {
	const finalId = id === null || id === undefined ? uuid.v4() : id;
	return {
		type: actionTypes.BINDER_ADD_ORDER_ITEM,
		binderId,
		id: finalId,
		itemType,
		label,
		insertAfterId,
		children,
	};
}

export function addBinderFolderWithTabs(binderId, label, itemId, childrenTabs) {
	return (dispatch, getState) => {
		const state = getState();
		const binder = state.binders[binderId];

		if (!binder) {
			return;
		}

		const finalId = itemId === null || itemId === undefined ? uuid.v4() : itemId;
		dispatch(binderTabsActions.setBinderTabs(childrenTabs));
		dispatch({
			type: actionTypes.BINDER_ADD_FOLDER_WITH_TABS,
			binderId,
			label,
			itemId: finalId,
			childrenTabs,
		});
	};
}

export function saveBinderInformation(binderId, title, description, clientcode, ownerId) {
	return (dispatch, getState) => {
		const currentState = getState();
		const currentBinder = currentState.binders[binderId];

		if (!currentBinder) {
			if (__DEV__) {
				console.warn("Attempting to save information for a binder that cannot be found");
			}
			return Promise.reject(binderErrors.BINDER_NOT_FOUND);
		}

		if (currentBinder.title !== title) {
			dispatch(setBinderTitle(binderId, title));
		}

		if (currentBinder.description !== description) {
			dispatch(setBinderDescription(binderId, description));
		}

		if (currentBinder.clientcode !== clientcode) {
			dispatch(setBinderClientCode(binderId, clientcode));
		}

		const updatedState = getState();
		const updatedBinder = updatedState.binders[binderId];
		const binderTabs = updatedBinder.tabIds
			? updatedBinder.tabIds.map(tabId => updatedState.binderTabs[tabId])
			: [];
		const shouldSaveBinder =
			updatedBinder.id === reservedBinderIds.UNASSIGNED ||
			updatedBinder.dirty ||
			!!find(binderTabs, t => t.dirty);

		const saveBinderPromise = shouldSaveBinder
			? dispatch(saveBinderRequest(binderId, 0))
			: Promise.resolve(null);

		if (ownerId === null || ownerId === undefined) {
			return saveBinderPromise;
		}

		return saveBinderPromise
			.then(res => Promise.all([res, apiBinders.setOwner(binderId, ownerId)]))
			.then(([res]) => {
				if (binderId !== reservedBinderIds.UNASSIGNED) {
					dispatch(reloadBinder(binderId));
				}
				return Promise.resolve(res);
			});
	};
}

export function exportBindersAlertData(binderIdsArr, fileTitle) {
	const getBinderObservers = selectorFactory.createGetBinderObservers();
	return (dispatch, getState) => {
		const currentState = getState();
		if (!binderIdsArr || !binderIdsArr.length) {
			return;
		}

		const exportData = binderIdsArr.map(id => {
			const exportAlert = {};
			const binder = currentState.binders[id];
			manageAlertColumns.TABLE_COLUMNS.forEach(col => {
				let value = binder[col.columnName];
				if (
					col.columnName === manageAlertColumns.REDUX_DATA_FIELDS.startDate ||
					col.columnName === manageAlertColumns.REDUX_DATA_FIELDS.lastEmail
				) {
					let dateString = dateUtil.getAbbreviatedDateString(value);
					dateString = dateString ? dateString.toLowerCase() : null;
					value = stringUtil.capitalizeFirst(dateString);
				} else if (col.columnName === manageAlertColumns.REDUX_DATA_FIELDS.frequency) {
					value = binderAlertRateLabels.ALERT_LABELS[value];
				} else if (col.columnName === manageAlertColumns.REDUX_DATA_FIELDS.recipients) {
					const observers = getBinderObservers(currentState, binder.id);
					value = observers
						.filter(o => o.alertRate !== binderAlertRates.NONE)
						.map(o => getObserverLabel(o));
				}

				if (value === null || value === undefined) {
					value = "";
				}
				exportAlert[col.label] = value;
			});
			return exportAlert;
		});

		fileDownloadUtil.downloadCSV(exportData, fileTitle);
	};
}

export function clearBinderTiles(binderId) {
	return (dispatch, getState) => {
		const state = getState();
		const binder = state.binders[binderId];
		if (!binder) {
			return;
		}

		const binderTabs = binder.tabIds
			? binder.tabIds.map(id => state.binderTabs[id]).filter(tab => !!tab)
			: [];
		let tileSectionIds = [];
		let tileIds = [];
		binderTabs.forEach(tab => {
			if (tab.sectionIdNames) {
				const tabSectionIds = tab.sectionIdNames.map(({ id }) => id).filter(id => !!id);
				tileSectionIds = [...tileSectionIds, ...tabSectionIds];

				const tabTileIds = tabSectionIds.reduce((acc, sectionId) => {
					const section = state.tileSections[sectionId];
					return !section || !section.tileIds ? acc : [...acc, ...section.tileIds];
				}, []);
				tileIds = [...tileIds, ...tabTileIds];
			}
		});

		if (tileSectionIds.length) {
			dispatch(tileSectionsActions.removeTileSections(tileSectionIds));
		}
		if (tileIds.length) {
			dispatch(tilesActions.removeTiles(tileIds));
		}
	};
}

export function setOwner(binderIds, userId) {
	return (dispatch, getState) => {
		if (!binderIds || !binderIds.length || !Number.isFinite(userId)) {
			return Promise.reject(new Error(binderErrors.BINDER_INVALID_OPERATION));
		}

		const state = getState();
		const editableBinders = binderIds
			.map(id => state.binders[id])
			.filter(binder => binder && binder.editable);

		if (!editableBinders.length) {
			return Promise.reject(new Error(binderErrors.BINDER_UNAUTHORIZED_OPERATION));
		}

		return Promise.map(editableBinders, (binder) =>
			apiBinders.setOwner(binder.id, userId).then(() => {
				dispatch(reloadBinder(binder.id));
			}),
		);
	};
}

export const getAllItemsIds = arr => {
	let ids = [];

	arr.forEach(obj => {
		if (obj.hasOwnProperty("id")) {
			ids.push(obj.id);
		}

		if (obj.hasOwnProperty("children")) {
			ids = ids.concat(getAllItemsIds(obj.children));
		}
	});

	return ids;
};
